import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { Truck, Lock, Phone, Mail, Loader2, Eye, EyeOff, Fingerprint } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDriverAuth } from '../context/DriverAuthContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import DriverInstallTip from '../components/DriverInstallTip';
import {
  passkeySupported,
  isPlatformAuthenticatorAvailable,
  checkDriverPasskey,
  loginDriverWithPasskey,
} from '../lib/passkey';

const FALLBACK_LOGO = '';

const DriverLogin = () => {
  const { token, login, applyToken } = useDriverAuth();
  const { settings } = useSettings();
  const [mode, setMode] = useState('phone'); // 'phone' | 'email'
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pkSupported, setPkSupported] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setPkSupported(passkeySupported());
    })();
  }, []);

  if (token) return <Navigate to="/driver" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(phone.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast({ title: 'Welcome, driver' });
      navigate('/driver', { replace: true });
    } else {
      toast({ title: 'Login failed', description: res.error });
    }
  };

  const passkeySignIn = async () => {
    const id = (phone || '').trim();
    if (!id) {
      toast({ title: 'Enter your phone or email first', description: 'Needed to look up your passkey.' });
      return;
    }
    setPkBusy(true);
    try {
      const has = await checkDriverPasskey(id);
      if (!has) {
        toast({ title: 'No passkey for this account', description: 'Sign in with password once, then enable a passkey from your dashboard.' });
        return;
      }
      const { access_token, driver } = await loginDriverWithPasskey(id);
      if (applyToken) applyToken(access_token, driver);
      toast({ title: 'Signed in with passkey ✓' });
      navigate('/driver', { replace: true });
    } catch (err) {
      toast({ title: 'Passkey sign-in failed', description: err?.response?.data?.detail || err?.message || 'Try password' });
    } finally {
      setPkBusy(false);
    }
  };

  return (
    <main className="min-h-screen w-full flex items-center justify-center px-5 py-12 bg-[#0B1220]">
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-emerald-500/20 blur-[120px]" />
      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center gap-3 mb-6">
          <img
            src={settings?.logo_image || FALLBACK_LOGO}
            alt="Mohammed Riaz & Partner LLC"
            className="h-16 w-auto rounded-2xl shadow-lg shadow-black/30 object-contain bg-white p-1.5"
            data-testid="driver-login-logo"
          />
          <div className="text-center">
            <div className="font-display text-xl font-bold text-white leading-none">Mohammed Riaz</div>
            <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-300 uppercase mt-1.5">
              Driver Portal
            </div>
          </div>
        </div>

        <DriverInstallTip />

        <div className="bg-[#111827] border border-white/10 rounded-3xl shadow-2xl p-8 backdrop-blur">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/40">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-300 uppercase">Sign in</div>
              <h1 className="font-display text-2xl text-white leading-tight">Welcome back</h1>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="driver-login-form">
            {/* Sign-in method toggle */}
            <div className="flex bg-[#0B1220] border border-white/10 rounded-full p-1 gap-1">
              <button
                type="button"
                onClick={() => { setMode('phone'); setPhone(''); }}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                  mode === 'phone'
                    ? 'bg-emerald-500 text-white shadow'
                    : 'text-slate-300 hover:text-white'
                }`}
                data-testid="driver-login-mode-phone"
              >
                <Phone className="w-3.5 h-3.5" /> Phone
              </button>
              <button
                type="button"
                onClick={() => { setMode('email'); setPhone(''); }}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                  mode === 'email'
                    ? 'bg-emerald-500 text-white shadow'
                    : 'text-slate-300 hover:text-white'
                }`}
                data-testid="driver-login-mode-email"
              >
                <Mail className="w-3.5 h-3.5" /> Email
              </button>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-300 uppercase">
                {mode === 'phone' ? 'Phone number' : 'Email address'}
              </label>
              <div className="relative mt-1.5">
                {mode === 'phone' ? (
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                ) : (
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                )}
                {mode === 'phone' ? (
                  <Input
                    key="phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    pattern="[0-9+\s]*"
                    placeholder="e.g. 99000111"
                    className="h-12 pl-10 rounded-xl bg-[#0B1220] border-white/10 text-white placeholder:text-slate-600"
                    data-testid="driver-login-phone"
                  />
                ) : (
                  <Input
                    key="email-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="h-12 pl-10 rounded-xl bg-[#0B1220] border-white/10 text-white placeholder:text-slate-600"
                    data-testid="driver-login-email"
                  />
                )}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-300 uppercase">Password</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input type={showPwd ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="h-12 pl-10 pr-12 rounded-xl bg-[#0B1220] border-white/10 text-white" data-testid="driver-login-password" />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-300 transition"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  data-testid="driver-login-toggle-password"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2"
              data-testid="driver-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sign in
            </Button>
          </form>
          {pkSupported && (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                <span className="flex-1 h-px bg-white/10" />
                or
                <span className="flex-1 h-px bg-white/10" />
              </div>
              <button
                type="button"
                onClick={passkeySignIn}
                disabled={pkBusy || !phone}
                className="w-full h-12 rounded-full border border-white/15 hover:border-emerald-400 hover:bg-emerald-500/10 text-white font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="driver-login-passkey"
              >
                {pkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4 text-emerald-300" />}
                Sign in with fingerprint / Face ID
              </button>
            </div>
          )}
          <div className="text-center text-xs text-slate-400 mt-5">
            New driver?{' '}
            <Link to="/driver/signup" className="text-emerald-300 font-semibold hover:underline" data-testid="driver-signup-link">
              Apply to drive
            </Link>
          </div>
        </div>
      </div>
      <PWAInstallPrompt />
    </main>
  );
};

export default DriverLogin;
